import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Table as ReactTable } from '@tanstack/react-table';
import { useLocation } from 'react-router-dom';
import debounce from 'just-debounce-it';
import Fuse from 'fuse.js';
import i18n, { DateFormatOptionsForExport } from '@/utils/i18n';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { DataTableFacetedFilter } from '@/views/Order/components/table/TableFacetedFilter';
import { DataTable } from '@/views/Events/EventOwnerDashboard/HandoutsReturnsTable/DataTable';
import {
  getTransactionColumns,
  getTransactionTypeName,
  TransactionRow,
} from './merchantTransactionsColumns';
import { Communicator } from '../../tools/Communication';
import { ContainerType } from '../Events/OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import convertDateToDateTimeLocalString from '@/utils/convertDateToDateTimeLocalString';
import { toCsvBlob } from '@/utils/data';
import { download, downloadJsonDataAsExcelFile } from '@/utils/download';
import { LayoutStoreInstance } from '../../LayoutStore';
import {
  CanAccessPartnerData,
  CanAccessUserData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CalendarIcon, ChevronDownIcon, PlusIcon, XIcon } from 'lucide-react';
import { ContainerLocationStatus } from '@/utils/containerLocation';

const TRANSACTIONS_PAGE_SIZE = 20;

const timeRangeTypesEnum: Record<string, number | undefined> = {
  all: undefined,
  today: 0,
  yesterday: 1,
  '7days': 7,
  '30days': 30,
  '60days': 60,
  '90days': 90,
};

type ContainerTypesResponseType = ContainerType[];

type ContainersType = {
  name: string;
  id: string;
  containerTypeImageUrl: string | null;
  typeId: number;
};

type TransactionEntryType = {
  timestamp: string;
  isInternal: boolean;
  containers: ContainersType[];
  userName: string;
  userId: string;
  externalIntegrationUserId: string | null;
  containerLocationId: number;
  holdingStatus: number | null;
  holdingReturnDeadline: string | null;
  holdingChargedAt: string | null;
};

type TransactionResponseType = {
  [index: string]: TransactionEntryType[];
};

/**
 * Flattens the grouped transaction response into one row per container, resolves
 * each container's type, and sorts newest-first (the default table order).
 * All filtering (type, container type, consumer, searches) happens later in
 * the `tableData` memo, so it is intentionally not done here.
 */
const transactionsToArray = (
  transactions: TransactionResponseType,
  containerTypes: ContainerTypesResponseType | undefined,
): TransactionRow[] => {
  let globalUniqueId = 0;

  return Object.keys(transactions)
    .flatMap((key) => {
      const currentTransactionType =
        key.indexOf('Checkouts') !== -1 ? 'checkout' : 'returns';

      return transactions[key].flatMap((cur) =>
        cur.containers.map((c) => {
          const uniqueId = globalUniqueId;
          globalUniqueId += 1;

          const containerType = containerTypes?.find(
            (ct) => ct.id === c.typeId,
          );

          return {
            ...c,
            containerType: containerType,
            containerTypeName: containerType?.name,
            timestamp: cur.timestamp,
            type: currentTransactionType,
            isInternal: cur.isInternal,
            userId: cur.userId,
            userName: cur.userName,
            externalIntegrationUserId: cur.externalIntegrationUserId,
            containerLocationId: cur.containerLocationId,
            holdingStatus: cur.holdingStatus ?? null,
            holdingReturnDeadline: cur.holdingReturnDeadline ?? null,
            holdingChargedAt: cur.holdingChargedAt ?? null,
            uniqueId,
          };
        }),
      );
    })
    .sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);

      // Primary sort: by timestamp desc
      if (dateA > dateB) {
        return -1;
      } else if (dateA < dateB) {
        return 1;
      }

      // Secondary sort: if timestamps are equal, sort by containerLocationId desc
      if (a.containerLocationId > b.containerLocationId) {
        return -1;
      } else if (a.containerLocationId < b.containerLocationId) {
        return 1;
      }

      return 0;
    });
};

/**
 * Toolbar filters, following the Orders toolbar model (OrderToolbar.tsx):
 * default filters are not removable — they only show an X (which clears the
 * value) once they hold one. Non-default filters live behind "+ Add a filter"
 * and always show an X, which removes them. Time range and date range are
 * always rendered because the fetch requires a bounded range.
 */
type TransactionFilterId =
  | 'containerName'
  | 'transactionType'
  | 'containerType'
  | 'externalUserId'
  | 'holdingState';

/**
 * Holding-state filter values. Both look at the guest's holding behind a checkout row, so
 * return rows never match. `open`: still with the guest (CheckedOut or Overdue, not yet
 * charged). `onInvoice`: charged as overdue in the current UTC month — the same rule the
 * per-user checkouts page uses for its "On the upcoming invoice" list.
 */
type HoldingStateFilter = '' | 'open' | 'onInvoice';

const DEFAULT_ACTIVE_FILTERS: TransactionFilterId[] = [
  'containerName',
  'transactionType',
  'containerType',
];

/** Filters rendered in the first toolbar row (like Orders' firstRowFilterIds). */
const FIRST_ROW_FILTER_IDS = new Set<TransactionFilterId>(['containerName']);

const REMOVABLE_FILTER_IDS = new Set<TransactionFilterId>([
  'externalUserId',
  'holdingState',
]);

const FILTER_LABELS: Record<TransactionFilterId, () => string> = {
  containerName: () => i18n.t('MerchantTransactions:containerName'),
  transactionType: () => i18n.t('MerchantTransactions:transactionType'),
  containerType: () => i18n.t('MerchantTransactions:containerType'),
  externalUserId: () =>
    i18n.t('MerchantTransactions:externalIntegrationUserId'),
  holdingState: () => i18n.t('MerchantTransactions:holdingState'),
};

/** The X segment attached to the right edge of a filter chip (Orders style). */
const RemoveFilterButton = ({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) => (
  <Button
    variant="outline"
    size="icon"
    className="tw:rounded-l-none tw:border-l-0 tw:size-8"
    onClick={onClick}
  >
    <XIcon className="tw:size-3" />
    <span className="tw:sr-only">{label}</span>
  </Button>
);

type MerchantAppAccountsTabNewProps = {
  vytalStoreGuid: string;
  title: string;
};

const MerchantTransactionsTab = ({
  vytalStoreGuid,
  title,
}: MerchantAppAccountsTabNewProps) => {
  const viewer = useContext(ViewerContext);
  const location = useLocation();
  // The URL of this transactions view, carried into the checkouts drill-down so
  // its breadcrumb/back can return to the exact tab the user came from.
  const fromPath = location.pathname + location.search;

  const currentDate = new Date();
  const lastWeek = new Date(currentDate);
  lastWeek.setDate(currentDate.getDate() - 7);

  const [startDate, setStartDate] = useState(lastWeek);
  const [endDate, setEndDate] = useState(currentDate);
  const [searchText, setSearchText] = useState('');
  const [
    searchTextExternalIntegrationUserId,
    setSearchTextExternalIntegrationUserId,
  ] = useState('');
  const [timeRangeType, setTimeRangeType] = useState<string>('7days');
  const [onlyConsumer, setOnlyConsumer] = useState(true);
  const [holdingState, setHoldingState] = useState<HoldingStateFilter>('');

  const [activeFilterIds, setActiveFilterIds] = useState<TransactionFilterId[]>(
    DEFAULT_ACTIVE_FILTERS,
  );
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState<
    string[]
  >([]);
  const [selectedContainerTypeNames, setSelectedContainerTypeNames] = useState<
    string[]
  >([]);

  const [containerTypes, setContainerTypes] =
    useState<ContainerTypesResponseType>();
  const [loading, setLoading] = useState(false);
  const [transactionsResponse, setTransactionsResponse] =
    useState<TransactionResponseType>();

  const canAccessUserData = CanAccessUserData(viewer);
  const canAccessPartnerData = CanAccessPartnerData(viewer);

  // Captured from the DataTable so export can read the fully-filtered rows.
  const tableRef = useRef<ReactTable<TransactionRow> | null>(null);

  // The holding-state columns cost three extra joins on the backend, so they are only
  // requested while the holding-state filter chip is on the toolbar.
  const includeHoldingState = activeFilterIds.includes('holdingState');

  const fetchTransactions = useCallback(
    debounce(
      async (
        vytalStoreGuid: string,
        startDate: Date,
        endDate: Date,
        includeHoldingState: boolean,
      ) => {
        setLoading(true);

        Communicator.PostData('api/3/Statistics/GetTransactionsInTimeRange', {
          timeFrom: startDate.toISOString(),
          timeTo: endDate.toISOString(),
          includeCheckouts: true,
          includeCheckins: true,
          storeId: vytalStoreGuid,
          includeHoldingState,
        })
          .then((response) => response.json())
          .then((data) => {
            setTransactionsResponse(data);
            setLoading(false);
          })
          .catch(() => {
            LayoutStoreInstance.setError(
              i18n.t('MerchantTransactions:errorLoadingTransactions'),
            );

            setLoading(false);
          });
      },
      1000,
    ),
    [],
  );

  useEffect(() => {
    setLoading(true);

    Communicator.Get('container/GetAllContainerCategories', {})
      .then((response) => response.json())
      .then((data) => {
        const types: ContainerTypesResponseType = data.containerTypes;

        setContainerTypes(types);
      })
      .catch(() => {
        LayoutStoreInstance.setError(
          i18n.t('MerchantTransactions:errorLoadingContainerTypes'),
        );
      });

    fetchTransactions.cancel();
    fetchTransactions(vytalStoreGuid, startDate, endDate, includeHoldingState);

    return () => {
      fetchTransactions.cancel();
    };
  }, []);

  useEffect(() => {
    fetchTransactions.cancel();
    fetchTransactions(vytalStoreGuid, startDate, endDate, includeHoldingState);
  }, [startDate, endDate, includeHoldingState]);

  useEffect(() => {
    const selectedTimeRange = timeRangeTypesEnum[timeRangeType];

    if (selectedTimeRange !== undefined) {
      const end = new Date();
      const start = new Date(end);

      start.setDate(end.getDate() - selectedTimeRange);
      start.setHours(0, 0, 0, 0);

      setStartDate(start);
      setEndDate(end);
    }
  }, [timeRangeType]);

  const transactions = useMemo(
    () =>
      transactionsResponse
        ? transactionsToArray(transactionsResponse, containerTypes)
        : [],
    [transactionsResponse, containerTypes],
  );

  const hasExternalIntegrationUserIds = transactions.some(
    (t) => t.externalIntegrationUserId !== null,
  );

  // All toolbar filters run here, before the table, so the table (and the
  // export, which reads the table's sorted row model) always sees the final
  // filtered set.
  const tableData = useMemo(() => {
    let rows = onlyConsumer
      ? transactions.filter((t) => !t.isInternal)
      : transactions;

    if (selectedTransactionTypes.length > 0) {
      rows = rows.filter((t) => selectedTransactionTypes.includes(t.type));
    }

    if (selectedContainerTypeNames.length > 0) {
      rows = rows.filter(
        (t) =>
          t.containerTypeName != null &&
          selectedContainerTypeNames.includes(t.containerTypeName),
      );
    }

    const hasToManyTransactions = rows.length > 100000;

    if (searchText.length !== 0) {
      if (hasToManyTransactions) {
        rows = rows.filter((t) =>
          t.name.toLowerCase().startsWith(searchText.toLowerCase()),
        );
      } else {
        const fuse = new Fuse(rows, { keys: ['name'] });
        rows = fuse.search(searchText).map((result) => result.item);
      }
    }

    if (searchTextExternalIntegrationUserId.length !== 0) {
      rows = rows.filter(
        (t) =>
          t.externalIntegrationUserId !== null &&
          t.externalIntegrationUserId
            .toLowerCase()
            .startsWith(searchTextExternalIntegrationUserId.toLowerCase()),
      );
    }

    if (holdingState === 'open') {
      rows = rows.filter(
        (t) =>
          t.holdingStatus === ContainerLocationStatus.CheckedOut ||
          t.holdingStatus === ContainerLocationStatus.Overdue,
      );
    } else if (holdingState === 'onInvoice') {
      const now = new Date();
      const monthStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
      rows = rows.filter(
        (t) =>
          t.holdingStatus === ContainerLocationStatus.OverdueSold &&
          t.holdingChargedAt != null &&
          new Date(t.holdingChargedAt).getTime() >= monthStart,
      );
    }

    return rows;
  }, [
    transactions,
    onlyConsumer,
    selectedTransactionTypes,
    selectedContainerTypeNames,
    searchText,
    searchTextExternalIntegrationUserId,
    holdingState,
  ]);

  const columns = useMemo(
    () =>
      getTransactionColumns({
        canAccessPartnerData,
        canAccessUserData,
        hasExternalIntegrationUserIds,
        vytalStoreGuid,
        fromPath,
        showHoldingState: includeHoldingState,
      }),
    [
      canAccessPartnerData,
      canAccessUserData,
      hasExternalIntegrationUserIds,
      vytalStoreGuid,
      fromPath,
      includeHoldingState,
    ],
  );

  const addFilter = (id: TransactionFilterId) =>
    setActiveFilterIds((ids) => [...ids, id]);

  // Removing a chip also resets its value so a hidden filter can't keep
  // narrowing the table silently.
  const removeFilter = (id: TransactionFilterId) => {
    setActiveFilterIds((ids) => ids.filter((x) => x !== id));

    switch (id) {
      case 'containerName':
        setSearchText('');
        break;
      case 'externalUserId':
        setSearchTextExternalIntegrationUserId('');
        break;
      case 'transactionType':
        setSelectedTransactionTypes([]);
        break;
      case 'containerType':
        setSelectedContainerTypeNames([]);
        break;
      case 'holdingState':
        setHoldingState('');
        break;
    }
  };

  const addableFilterIds = (
    Object.keys(FILTER_LABELS) as TransactionFilterId[]
  ).filter(
    (id) =>
      REMOVABLE_FILTER_IDS.has(id) &&
      !activeFilterIds.includes(id) &&
      (id !== 'externalUserId' || hasExternalIntegrationUserIds),
  );

  /** Multi-select pills emit `'__all__'` when the selection is emptied. */
  const toSelectionArray = (value: string | string[]): string[] => {
    if (value === '__all__') return [];
    return Array.isArray(value) ? value : [value];
  };

  // Mirrors OrderToolbar's renderFilter: the X segment shows when the filter is
  // removable (X removes it) or holds a value (X clears it); the trigger loses
  // its right rounding only while the X is attached.
  const renderActiveFilter = (id: TransactionFilterId) => {
    const label = FILTER_LABELS[id]();
    const isRemovable = REMOVABLE_FILTER_IDS.has(id);

    const hasValue = {
      containerName: searchText !== '',
      externalUserId: searchTextExternalIntegrationUserId !== '',
      transactionType: selectedTransactionTypes.length > 0,
      containerType: selectedContainerTypeNames.length > 0,
      holdingState: holdingState !== '',
    }[id];

    const showX = isRemovable || hasValue;

    const clearValue = () => {
      switch (id) {
        case 'containerName':
          setSearchText('');
          break;
        case 'externalUserId':
          setSearchTextExternalIntegrationUserId('');
          break;
        case 'transactionType':
          setSelectedTransactionTypes([]);
          break;
        case 'containerType':
          setSelectedContainerTypeNames([]);
          break;
        case 'holdingState':
          setHoldingState('');
          break;
      }
    };

    const xButton = showX && (
      <RemoveFilterButton
        onClick={() => (isRemovable ? removeFilter(id) : clearValue())}
        label={isRemovable ? `Remove ${label} filter` : `Clear ${label} filter`}
      />
    );

    switch (id) {
      case 'containerName':
        return (
          <div key={id} className="tw:flex tw:items-center tw:gap-0">
            <Input
              id="containerName"
              type="text"
              placeholder={label}
              value={searchText}
              onChange={(event) => setSearchText(event.currentTarget.value)}
              className={cn('tw:h-8 tw:w-52', showX && 'tw:rounded-r-none')}
            />
            {xButton}
          </div>
        );
      case 'externalUserId':
        return (
          <div key={id} className="tw:flex tw:items-center tw:gap-0">
            <Input
              id="externalIntegrationUserId"
              type="text"
              placeholder={label}
              value={searchTextExternalIntegrationUserId}
              onChange={(event) =>
                setSearchTextExternalIntegrationUserId(
                  event.currentTarget.value,
                )
              }
              className={cn('tw:h-8 tw:w-64', showX && 'tw:rounded-r-none')}
            />
            {xButton}
          </div>
        );
      case 'transactionType':
        return (
          <div key={id} className="tw:flex tw:items-center tw:gap-0">
            <DataTableFacetedFilter
              title={label}
              multiple
              options={[
                {
                  getLabel: () => i18n.t('MerchantTransactions:checkout'),
                  value: 'checkout',
                },
                {
                  getLabel: () => i18n.t('MerchantTransactions:return'),
                  value: 'returns',
                },
              ]}
              value={selectedTransactionTypes}
              onValueChange={(value) =>
                setSelectedTransactionTypes(toSelectionArray(value))
              }
              triggerClassName={showX && 'tw:rounded-r-none'}
            />
            {xButton}
          </div>
        );
      case 'containerType':
        return (
          <div key={id} className="tw:flex tw:items-center tw:gap-0">
            <DataTableFacetedFilter
              title={label}
              multiple
              options={(containerTypes ?? []).map((ct) => ({
                getLabel: () => ct.name,
                value: ct.name,
              }))}
              value={selectedContainerTypeNames}
              onValueChange={(value) =>
                setSelectedContainerTypeNames(toSelectionArray(value))
              }
              triggerClassName={showX && 'tw:rounded-r-none'}
            />
            {xButton}
          </div>
        );
      case 'holdingState':
        return (
          <div key={id} className="tw:flex tw:items-center tw:gap-0">
            <DataTableFacetedFilter
              title={label}
              options={[
                {
                  getLabel: () =>
                    i18n.t('MerchantTransactions:holdingStateOpen'),
                  value: 'open',
                },
                {
                  getLabel: () =>
                    i18n.t('MerchantTransactions:holdingStateOnInvoice'),
                  value: 'onInvoice',
                },
              ]}
              value={holdingState === '' ? '__all__' : holdingState}
              onValueChange={(value) =>
                setHoldingState(
                  value === '__all__' ? '' : (value as HoldingStateFilter),
                )
              }
              triggerClassName={showX && 'tw:rounded-r-none'}
            />
            {xButton}
          </div>
        );
    }
  };

  function toJson(results: any[]) {
    if (results == null) {
      return [];
    }

    const headers: Record<string, string> = {
      name: i18n.t('MerchantTransactions:containerName'),
      containerTypeName: i18n.t('MerchantTransactions:containerType'),
      timestamp: i18n.t('MerchantTransactions:transactionDate'),
      type: i18n.t('MerchantTransactions:transactionType'),
      id: i18n.t('MerchantTransactions:containerId'),
      shortId: i18n.t('MerchantTransactions:shortId'),
    };

    if (canAccessUserData) {
      headers.userId = i18n.t('MerchantTransactions:userId');
      headers.userName = i18n.t('Common:userName');
    }
    const headerKeys = Object.keys(headers);

    return results.map((transaction) => {
      if (transaction == null) {
        return null;
      }

      const transactionCopy = { ...transaction };

      transactionCopy.timestamp = new Date(
        transactionCopy.timestamp,
      ).toLocaleDateString('de', DateFormatOptionsForExport);

      transactionCopy.type = getTransactionTypeName(transactionCopy);

      return Object.entries(transactionCopy).reduce(
        (acc: any, [key, value]) => {
          if (!headerKeys.includes(key)) {
            return acc;
          }

          if (Array.isArray(value)) {
            acc[headers[key]] = value;
          } else {
            if (key !== '__typename') {
              acc[headers[key]] = value;
            }
          }

          return acc;
        },
        {},
      );
    });
  }

  // Every row matching the active filters (search, date range, consumer toggle
  // AND the table's faceted pills), across all pages — not just the current one.
  function getExportRows(): TransactionRow[] {
    const table = tableRef.current;
    if (table) {
      return table.getSortedRowModel().rows.map((row) => row.original);
    }
    return tableData;
  }

  function exportCSV() {
    const blob = toCsvBlob(toJson(getExportRows()));

    download(blob, `transactions_${new Date().toISOString()}.csv`);
  }

  async function exportExcel() {
    const jsonData = toJson(getExportRows());
    downloadJsonDataAsExcelFile(
      jsonData,
      `transactions_${new Date().toISOString()}`,
    );
  }

  // data-no-bs: this tab is fully shadcn — opt out of the scoped Bootstrap
  // that BootstrapRoute / the merchant page wrap around it, whose element
  // resets would override the Tailwind styling (see postcss.config.cjs).
  // No ContentSection/EditSection on purpose: the tab renders directly on the
  // page background with a single title, like the Ops Orders page.
  return (
    <div className="tw:flex tw:flex-col tw:gap-8" data-no-bs>
      <div className="tw:flex tw:flex-col tw:gap-2">
        <h1 className="tw:text-4xl tw:font-semibold">{title}</h1>
        <p className="tw:leading-6">
          {i18n.t('MerchantTransactions:pageDescription')}
        </p>
      </div>

      <Separator />

      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          {activeFilterIds
            .filter((id) => FIRST_ROW_FILTER_IDS.has(id))
            .map(renderActiveFilter)}

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="tw:border-dashed">
                <CalendarIcon />
                {i18n.t('Common:dateTime')}
                <Separator
                  orientation="vertical"
                  className="tw:mx-2 tw:h-full"
                />
                <Badge
                  variant="secondary"
                  className="tw:rounded-sm tw:px-1 tw:font-normal"
                >
                  {startDate.toLocaleDateString(i18n.language)} –{' '}
                  {endDate.toLocaleDateString(i18n.language)}
                </Badge>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="tw:flex tw:w-auto tw:flex-col tw:gap-4"
              align="start"
            >
              <div className="tw:flex tw:flex-col tw:gap-1">
                <Label htmlFor="startDate">{i18n.t('Common:startDate')}</Label>

                <Input
                  id="startDate"
                  type="datetime-local"
                  aria-invalid={endDate < startDate}
                  value={convertDateToDateTimeLocalString(startDate)}
                  onChange={(event) => {
                    // The native picker's Clear empties the value; keep the last date.
                    if (!event.target.value) return;
                    setTimeRangeType('all');
                    setStartDate(new Date(event.target.value));
                  }}
                />
              </div>

              <div className="tw:flex tw:flex-col tw:gap-1">
                <Label htmlFor="endDate">{i18n.t('Common:endDate')}</Label>

                <Input
                  id="endDate"
                  type="datetime-local"
                  value={convertDateToDateTimeLocalString(endDate)}
                  onChange={(event) => {
                    // The native picker's Clear empties the value; keep the last date.
                    if (!event.target.value) return;
                    setTimeRangeType('all');
                    setEndDate(new Date(event.target.value));
                  }}
                />
              </div>
            </PopoverContent>
          </Popover>

          <div className="tw:flex tw:items-center tw:gap-2">
            <Checkbox
              id="onlyConsumerCheckbox"
              checked={onlyConsumer}
              onCheckedChange={(checked) => setOnlyConsumer(checked === true)}
            />
            <Label htmlFor="onlyConsumerCheckbox">
              {i18n.t('MerchantTransactions:onlyConsumer')}
            </Label>
          </div>
        </div>

        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          {activeFilterIds
            .filter((id) => !FIRST_ROW_FILTER_IDS.has(id))
            .map(renderActiveFilter)}

          <div className="tw:flex tw:items-center tw:gap-0">
            <DataTableFacetedFilter
              title={i18n.t('MerchantTransactions:timeRange')}
              options={[
                {
                  getLabel: () => i18n.t('MerchantTransactions:today'),
                  value: 'today',
                },
                {
                  getLabel: () => i18n.t('MerchantTransactions:yesterday'),
                  value: 'yesterday',
                },
                {
                  getLabel: () => i18n.t('MerchantTransactions:7days'),
                  value: '7days',
                },
                {
                  getLabel: () => i18n.t('MerchantTransactions:30days'),
                  value: '30days',
                },
                {
                  getLabel: () => i18n.t('MerchantTransactions:60days'),
                  value: '60days',
                },
                {
                  getLabel: () => i18n.t('MerchantTransactions:90days'),
                  value: '90days',
                },
              ]}
              value={timeRangeType === 'all' ? '__all__' : timeRangeType}
              onValueChange={(value) =>
                setTimeRangeType(value === '__all__' ? 'all' : value)
              }
              triggerClassName={timeRangeType !== 'all' && 'tw:rounded-r-none'}
            />
            {timeRangeType !== 'all' && (
              <RemoveFilterButton
                onClick={() => setTimeRangeType('all')}
                label={`Clear ${i18n.t('MerchantTransactions:timeRange')} filter`}
              />
            )}
          </div>

          {addableFilterIds.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm">
                  <PlusIcon />
                  {i18n.t('Order:toolbarAddFilter')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {addableFilterIds.map((id) => (
                  <DropdownMenuItem key={id} onClick={() => addFilter(id)}>
                    {FILTER_LABELS[id]()}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <Separator />

      <DataTable
        columns={columns}
        data={tableData}
        isLoading={loading}
        opts={{
          pageSize: TRANSACTIONS_PAGE_SIZE,
          paginationVariant: 'numbered',
          initialColumnVisibility: { id: false, shortId: false },
          viewOptionsVariant: 'icon',
          tableFooter: ({ table }) => {
            tableRef.current = table;
            return null;
          },
          ToolbarRightComponent: (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={loading}>
                  {i18n.t('ExportButton:exportAs')}
                  <ChevronDownIcon className="tw:size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportCSV()}>
                  {i18n.t('ExportButton:exportCSV')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportExcel()}>
                  {i18n.t('ExportButton:exportExcel')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ),
        }}
      />
    </div>
  );
};

export default MerchantTransactionsTab;
